<script setup lang="ts">
import { ArrowRight } from 'lucide-vue-next'

const props = defineProps<{
  error: {
    statusCode: number
    message: string
  }
}>()

const is404 = computed(() => props.error.statusCode === 404)

useHead({
  meta: [{ name: 'robots', content: 'noindex, nofollow' }],
})

const handleBack = () => clearError({ redirect: '/' })
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-dark-500 flex items-center justify-center relative overflow-hidden">
    <!-- Ambient glow -->
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[800px] md:h-[800px] bg-blu-500/[0.04] rounded-full blur-[120px] pointer-events-none" />

    <!-- Scan lines overlay -->
    <div class="scan-lines fixed inset-0 pointer-events-none z-20" aria-hidden="true" />

    <div class="max-w-2xl mx-auto px-6 text-center relative z-10">
      <!-- Glitch status code -->
      <div class="glitch-wrapper relative mb-8 select-none" :data-text="error.statusCode">
        <span class="error-code text-[8rem] md:text-[12rem] font-bold leading-none tracking-tighter text-white/10">
          {{ error.statusCode }}
        </span>
        <!-- Glitch layers -->
        <span class="glitch-layer glitch-r absolute inset-0 text-[8rem] md:text-[12rem] font-bold leading-none tracking-tighter" aria-hidden="true">
          {{ error.statusCode }}
        </span>
        <span class="glitch-layer glitch-b absolute inset-0 text-[8rem] md:text-[12rem] font-bold leading-none tracking-tighter" aria-hidden="true">
          {{ error.statusCode }}
        </span>
      </div>

      <h1 class="text-2xl md:text-3xl font-bold text-slate-700 dark:text-white mb-4">
        <template v-if="is404">
          This page doesn't exist
        </template>
        <template v-else>
          Something went wrong
        </template>
      </h1>

      <p class="text-lg text-slate-700/50 dark:text-white/40 mb-10 max-w-md mx-auto">
        <template v-if="is404">
          The page you're looking for has been moved, deleted, or never existed in the first place.
        </template>
        <template v-else>
          We hit an unexpected error. If this keeps happening, get in touch and we'll sort it out.
        </template>
      </p>

      <div class="flex items-center justify-center gap-4">
        <button
          @click="handleBack"
          class="inline-flex items-center gap-3 px-8 py-4 bg-blu-500 text-slate-700 font-semibold rounded-full hover:bg-blu-400 transition-colors"
        >
          Back to home
          <ArrowRight class="w-4 h-4" />
        </button>

        <NuxtLink
          v-if="is404"
          to="/contact"
          class="inline-flex items-center gap-2 px-6 py-4 text-slate-700/60 dark:text-white/50 hover:text-blu-400 transition-colors font-medium"
        >
          Get in touch
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Red channel layer */
.glitch-r {
  color: rgba(255, 0, 80, 0);
  text-shadow: 2px 0 rgba(255, 0, 80, 0.4);
  animation: glitchR 4s ease-in-out infinite;
  clip-path: inset(0 0 0 0);
}

/* Cyan/blue channel layer */
.glitch-b {
  color: rgba(0, 200, 255, 0);
  text-shadow: -2px 0 rgba(0, 200, 255, 0.4);
  animation: glitchB 4s ease-in-out infinite;
  clip-path: inset(0 0 0 0);
}

@keyframes glitchR {
  0%, 85%, 100% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  86% {
    clip-path: inset(20% 0 60% 0);
    transform: translate(4px, -2px);
  }
  87% {
    clip-path: inset(70% 0 5% 0);
    transform: translate(-3px, 1px);
  }
  88% {
    clip-path: inset(40% 0 30% 0);
    transform: translate(6px, 0);
  }
  89% {
    clip-path: inset(10% 0 75% 0);
    transform: translate(-2px, 2px);
  }
  90% {
    clip-path: inset(55% 0 20% 0);
    transform: translate(3px, -1px);
  }
  91% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  /* Second burst */
  94% {
    clip-path: inset(65% 0 15% 0);
    transform: translate(-5px, 1px);
  }
  95% {
    clip-path: inset(5% 0 80% 0);
    transform: translate(4px, -1px);
  }
  96% {
    clip-path: inset(35% 0 40% 0);
    transform: translate(-3px, 0);
  }
  97% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
}

@keyframes glitchB {
  0%, 85%, 100% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  86% {
    clip-path: inset(60% 0 10% 0);
    transform: translate(-4px, 1px);
  }
  87% {
    clip-path: inset(5% 0 70% 0);
    transform: translate(3px, -2px);
  }
  88% {
    clip-path: inset(30% 0 45% 0);
    transform: translate(-5px, 0);
  }
  89% {
    clip-path: inset(75% 0 10% 0);
    transform: translate(2px, 1px);
  }
  90% {
    clip-path: inset(15% 0 55% 0);
    transform: translate(-3px, -1px);
  }
  91% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  /* Second burst — offset from red */
  93% {
    clip-path: inset(45% 0 25% 0);
    transform: translate(5px, -1px);
  }
  94% {
    clip-path: inset(10% 0 65% 0);
    transform: translate(-4px, 2px);
  }
  95% {
    clip-path: inset(70% 0 5% 0);
    transform: translate(3px, 0);
  }
  96% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
}

/* Base text also gets a brief flash */
.error-code {
  animation: baseGlitch 4s ease-in-out infinite;
}

@keyframes baseGlitch {
  0%, 85%, 91%, 97%, 100% {
    transform: translate(0);
    opacity: 1;
  }
  87% {
    transform: translate(2px, -1px) skewX(-1deg);
    opacity: 0.8;
  }
  89% {
    transform: translate(-1px, 1px) skewX(0.5deg);
    opacity: 0.9;
  }
  95% {
    transform: translate(1px, 0) skewX(-0.5deg);
    opacity: 0.85;
  }
}

.scan-lines {
  background: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    rgba(0, 212, 255, 0.02) 2px,
    rgba(0, 212, 255, 0.02) 4px
  );
  opacity: 0.5;
}
</style>
